import { Button, Flex, NumberInput, SegmentedControl, Text, useMantineTheme } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useVestingTermsContext } from '@dealstack/frontend/shared/components/VestingTerms/VestingTermsContext.tsx';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { VestingSharedStyles } from '@dealstack/frontend/shared/components/VestingTerms/VestingTerms.styles.tsx';
import { PerformanceVestingMetricType } from '@dealstack/shared/lib/generated';
import { getPerformanceMetricTypeLabel } from '@dealstack/frontend/shared/components/VestingTerms/helpers/vesting-messages.ts';
import { PerformanceVestingAccessedFor } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceVestingAccessedFor.tsx';
import { PerformanceRatchetTiersList } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceRatchetTiersList/PerformanceRatchetTiersList.tsx';
import { PerformanceVestingStyles } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceVesting.styles.tsx';
import { ConfirmationModalV3 } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModalV3.tsx';
import { VestingCard } from '@dealstack/frontend/shared/components/VestingCard/VestingCard.tsx';

const MetricTypeLabel = ({ type }: { type: PerformanceVestingMetricType }) => {
	const tCommonUi = useTranslationCommonUi();

	return (
		<Flex align="center" gap="0.5rem" justify="start">
			<Text size="md" style={{ whiteSpace: 'nowrap' }}>
				{getPerformanceMetricTypeLabel(tCommonUi, type)}
			</Text>
		</Flex>
	);
};

const METRIC_TYPE_OPTIONS = Object.values(PerformanceVestingMetricType).map((value) => ({
	value,
	label: <MetricTypeLabel type={value} />,
}));

export const PerformanceVesting = memo(() => {
	const tCommonUi = useTranslationCommonUi();
	const {
		currentVestingTerm,
		handlePerformancePercentageUpdate,
		onMetricTypeChange,
		onMeasurementSubjectsChange,
		onTiersChange,
		personOptions,
	} = useVestingTermsContext();
	const theme = useMantineTheme();

	const [performancePercentageError, setPerformancePercentageError] = useState<string>();
	const [pendingMetricChange, setPendingMetricChange] =
		useState<PerformanceVestingMetricType | null>(null);

	useEffect(() => {
		setPerformancePercentageError(undefined);
		setPendingMetricChange(null);
	}, [currentVestingTerm.id]);

	const isEnabled = currentVestingTerm.performancePercentage > 0;
	const hasNoUnvestedRoom = !isEnabled && currentVestingTerm.unvestedPercentage === 0;

	const currentMetricType =
		currentVestingTerm.performanceVestingTerm?.metricType ?? PerformanceVestingMetricType.MOIC;

	const measurementSubjects = currentVestingTerm.performanceVestingTerm?.measurementSubjects;
	const currentSubjectIds = useMemo(
		() => measurementSubjects?.map((s) => s.personId) ?? [],
		[measurementSubjects]
	);

	const serverMetricType = currentVestingTerm.performanceVestingTerm?.metricType;
	const serverTiers = currentVestingTerm.performanceVestingTerm?.hurdles;
	const currentTiers = useMemo(
		() => (serverMetricType === currentMetricType ? serverTiers ?? [] : []),
		[serverMetricType, currentMetricType, serverTiers]
	);

	const handleMetricChange = (value: string): void => {
		const next = value as PerformanceVestingMetricType;

		if (next === currentMetricType) {
			return;
		}

		if (currentTiers.length > 0) {
			setPendingMetricChange(next);
			return;
		}

		onMetricTypeChange(next);
	};

	return (
		<PerformanceVestingStyles>
			<VestingSharedStyles>
				<VestingCard
					title={tCommonUi('vesting_terms.performance_vesting_title')}
					titleSize="lg"
					titleWeight={800}
					icon={<Icon variant="line-chart" size="vlg" />}
					expandable
					expanded={isEnabled}
					onExpandChange={(expanded) => {
						const { error } = handlePerformancePercentageUpdate(
							currentVestingTerm.id,
							expanded ? currentVestingTerm.unvestedPercentage : 0
						);
						setPerformancePercentageError(error ?? undefined);
					}}
					expandDisabled={hasNoUnvestedRoom}
					expandDisabledTooltip={tCommonUi('vesting_terms.performance.no_room')}
				>
					<div className="inner">
						<div className="container">
							<Flex justify="space-between" align="center" className="percentage-row">
								<Text fw={600} c="dark.8">
									{tCommonUi('vesting_terms.performance_vesting_percentage')}
								</Text>

								<div className="percentage-input">
									<NumberInput
										name="performancePercentage"
										value={currentVestingTerm.performancePercentage}
										onChange={(value) => {
											const { error } = handlePerformancePercentageUpdate(
												currentVestingTerm.id,
												value
											);
											setPerformancePercentageError(error ?? undefined);
										}}
										min={0}
										max={100}
										hideControls
										rightSection={<Icon variant="percentage" size="lg" />}
										error={performancePercentageError}
									/>
								</div>
							</Flex>

							<Flex direction="column" gap="1rem" className="performance-controls">
								<div className="metric-section">
									<Text fw={600} c="dark.8" mb="0.25rem">
										{tCommonUi('vesting_terms.performance.metric_label')}
									</Text>

									<SegmentedControl
										value={currentMetricType}
										onChange={handleMetricChange}
										data={METRIC_TYPE_OPTIONS}
										bg="white.0"
										styles={{
											control: {
												border: 'none !important',
											},
											indicator: {
												backgroundColor: theme.colors.blue[0],
												border: `1px solid ${theme.colors.blue[6]}`,
											},
											label: {
												padding: '0.5rem',
												color: theme.colors.dark[9],
											},
										}}
									/>
								</div>

								<PerformanceVestingAccessedFor
									selectedPersonIds={currentSubjectIds}
									personOptions={personOptions}
									onChange={onMeasurementSubjectsChange}
								/>

								<PerformanceRatchetTiersList
									tiers={currentTiers}
									metricType={currentMetricType}
									onSaveTiers={onTiersChange}
								/>
							</Flex>
						</div>
					</div>
				</VestingCard>
			</VestingSharedStyles>

			{pendingMetricChange && (
				<ConfirmationModalV3
					title={tCommonUi('vesting_terms.performance.metric_change_warning.title')}
					description={tCommonUi(
						'vesting_terms.performance.metric_change_warning.description'
					)}
					state="warning"
					children={
						<Flex gap="xs">
							<Button
								size="md"
								variant="secondary"
								onClick={() => setPendingMetricChange(null)}
							>
								{tCommonUi('vesting_terms.performance.metric_change_warning.back')}
							</Button>

							<Button
								size="md"
								variant="subtle"
								onClick={() => {
									onMetricTypeChange(pendingMetricChange);
									setPendingMetricChange(null);
								}}
							>
								{tCommonUi(
									'vesting_terms.performance.metric_change_warning.confirm'
								)}
							</Button>
						</Flex>
					}
				/>
			)}
		</PerformanceVestingStyles>
	);
});
